import { Link } from 'react-router-dom';
import { shell } from '../shell/shellClasses';
import type { ActionPriority, DecisionAction } from './types';
import { PRIORITY_LABEL } from './types';

const priorityStyle: Record<ActionPriority, string> = {
  critical:
    'border-[var(--shell-danger)]/40 bg-[var(--shell-danger)]/10 text-[var(--shell-danger)]',
  important:
    'border-[var(--shell-warning)]/40 bg-[var(--shell-warning)]/10 text-[var(--shell-warning)]',
  opportunity:
    'border-[var(--shell-success)]/40 bg-[var(--shell-success)]/10 text-[var(--shell-success)]',
  information: `border-[var(--shell-border)] ${shell.subtle}`,
};

function fmtTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

export function ActionCard({
  action,
  pinned,
  bookmarked,
  completed,
  onPin,
  onBookmark,
  onComplete,
  onDismiss,
  onRestore,
}: {
  action: DecisionAction;
  pinned: boolean;
  bookmarked: boolean;
  completed: boolean;
  onPin: () => void;
  onBookmark: () => void;
  onComplete: () => void;
  onDismiss: () => void;
  onRestore?: () => void;
}) {
  return (
    <article
      className={`rounded-[var(--shell-radius-md)] border px-3 py-3 ${shell.border} ${shell.surfaceElevated}`}
      data-action-id={action.id}
      data-priority={action.priority}
      aria-label={`${PRIORITY_LABEL[action.priority]}: ${action.title}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`rounded-[var(--shell-radius-sm)] border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${priorityStyle[action.priority]}`}
            >
              {PRIORITY_LABEL[action.priority]}
            </span>
            <span className={`text-[10px] uppercase tracking-wide ${shell.subtle}`}>
              {action.businessArea}
            </span>
            <span className={`text-[10px] tabular-nums ${shell.subtle}`}>
              conf {action.confidence}%
            </span>
            {pinned && (
              <span className={`text-[10px] font-medium ${shell.accent}`}>pinned</span>
            )}
            {bookmarked && (
              <span className={`text-[10px] font-medium ${shell.accent}`}>bookmarked</span>
            )}
            {completed && (
              <span className="text-[10px] font-medium text-[var(--shell-success)]">
                completed
              </span>
            )}
          </div>

          <h3 className="text-sm font-medium text-[var(--shell-fg)]">{action.title}</h3>
          <p className={`text-xs leading-relaxed ${shell.muted}`}>{action.description}</p>
          <p className={`text-xs leading-relaxed ${shell.subtle}`}>
            <span className="font-medium text-[var(--shell-fg-muted)]">Evidence: </span>
            {action.evidence}
          </p>

          <dl className={`grid gap-x-4 gap-y-0.5 text-[11px] sm:grid-cols-2 ${shell.subtle}`}>
            <div>
              <dt className="inline text-[var(--shell-fg-muted)]">Workspace: </dt>
              <dd className="inline">{action.sourceWorkspace}</dd>
            </div>
            <div>
              <dt className="inline text-[var(--shell-fg-muted)]">Created: </dt>
              <dd className="inline tabular-nums">{fmtTime(action.createdAt)}</dd>
            </div>
            <div>
              <dt className="inline text-[var(--shell-fg-muted)]">Updated: </dt>
              <dd className="inline tabular-nums">{fmtTime(action.updatedAt)}</dd>
            </div>
            {(action.sellerLabel || action.marketplace) && (
              <div>
                <dt className="inline text-[var(--shell-fg-muted)]">Scope: </dt>
                <dd className="inline">
                  {[action.sellerLabel, action.marketplace].filter(Boolean).join(' · ')}
                </dd>
              </div>
            )}
          </dl>
        </div>

        <div className="flex w-full shrink-0 flex-col gap-1.5 sm:w-auto sm:flex-row sm:flex-wrap">
          <Link
            to={action.workspacePath}
            className={`${shell.control} ${shell.focusRing} inline-flex min-h-11 items-center justify-center px-3 text-xs font-medium`}
          >
            {action.quickActionLabel}
          </Link>
          <Link
            to={action.workspacePath}
            className={`${shell.control} ${shell.focusRing} inline-flex min-h-11 items-center justify-center px-3 text-xs`}
          >
            Open workspace
          </Link>
          <button
            type="button"
            className={`${shell.control} ${shell.focusRing} min-h-11 px-3 text-xs`}
            onClick={onPin}
            aria-pressed={pinned}
          >
            {pinned ? 'Unpin' : 'Pin'}
          </button>
          <button
            type="button"
            className={`${shell.control} ${shell.focusRing} min-h-11 px-3 text-xs`}
            onClick={onBookmark}
            aria-pressed={bookmarked}
          >
            {bookmarked ? 'Unbookmark' : 'Bookmark'}
          </button>
          {!completed ? (
            <button
              type="button"
              className={`${shell.control} ${shell.focusRing} min-h-11 px-3 text-xs`}
              onClick={onComplete}
            >
              Mark complete
            </button>
          ) : (
            onRestore && (
              <button
                type="button"
                className={`${shell.control} ${shell.focusRing} min-h-11 px-3 text-xs`}
                onClick={onRestore}
              >
                Restore
              </button>
            )
          )}
          {!completed && (
            <button
              type="button"
              className={`${shell.control} ${shell.focusRing} min-h-11 px-3 text-xs`}
              onClick={onDismiss}
            >
              Dismiss
            </button>
          )}
        </div>
      </div>
    </article>
  );
}
